import { useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import {
  canReadNativeContacts,
  checkNativeContactsPermission,
  getNativeContactsPlugin,
  mapNativeContactPermission,
  notifyNativeContactsPermissionChanged,
  requestNativeContactsPermission,
  
  type NativeContact,
  type NativeContactsPlugin as NativeContactPlugin,
} from '@/lib/native-contacts';

export type ContactPermission = 'prompt' | 'prompt-with-rationale' | 'granted' | 'limited' | 'denied';

export interface DeviceContact {
  name: string;
  phoneNumbers: string[];
}

interface MatchedUser {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
}

type WebContact = { name?: string[]; tel?: string[] };
type WebContactsNavigator = Navigator & {
  contacts?: { select: (props: string[], opts: { multiple: boolean }) => Promise<WebContact[]> };
};

/** SHA-256 hash a string using Web Crypto API */
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Normalize phone number to E.164-ish format for consistent hashing */
function normalizePhone(phone: string, defaultCountryCode = '61'): string {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (!digits.startsWith('+')) {
    if (digits.startsWith('0')) {
      digits = defaultCountryCode + digits.slice(1);
    }
    digits = '+' + digits;
  } else {
    digits = '+' + digits;
  }
  return digits;
}

const isNative = Capacitor.isNativePlatform();

/** Check if web Contact Picker API is available */
function hasWebContactPicker(): boolean {
  return 'contacts' in navigator && 'ContactsManager' in window;
}

export function useDeviceContacts() {
  const [permission, setPermission] = useState<ContactPermission>('prompt');
  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [matches, setMatches] = useState<MatchedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const contactsPluginRef = useRef<NativeContactPlugin | null>(null);

  // Supported on native OR web Contact Picker API (Chrome Android)
  const isSupported = isNative || hasWebContactPicker();

  const getPlugin = useCallback(async () => {
    if (contactsPluginRef.current) return contactsPluginRef.current;
    contactsPluginRef.current = await getNativeContactsPlugin();
    return contactsPluginRef.current;
  }, []);

  const checkPermission = useCallback(async (): Promise<ContactPermission> => {
    if (isNative) {
      try {
        const status = await checkNativeContactsPermission();
        setPermission(status);
        return status;
      } catch {
        setPermission('denied');
        return 'denied';
      }
    }
    // Web Contact Picker: permission is implicit (user picks via OS dialog)
    if (hasWebContactPicker()) {
      setPermission('prompt');
      return 'prompt';
    }
    setPermission('denied');
    return 'denied';
  }, [getPlugin]);

  const requestPermission = useCallback(async (): Promise<ContactPermission> => {
    if (isNative) {
      // Let errors propagate so the caller can show the real native message
      // (e.g. missing NSContactsUsageDescription, bridge timeout) instead of
      // silently flipping to 'denied' and leaving the UI ambiguous.
      const status = await requestNativeContactsPermission();
      setPermission(status);
      notifyNativeContactsPermissionChanged(status);
      return status;
    }
    // Web: permission is granted by user selecting contacts in the picker
    if (hasWebContactPicker()) {
      setPermission('granted');
      return 'granted';
    }
    setPermission('denied');
    return 'denied';
  }, [getPlugin]);

  /** Read native contacts only when permission is already granted/limited. Never triggers an OS prompt.
   *  Large iPhone address books (1k+) can take 20–40s for the native bridge to
   *  serialize across the JSI boundary. We use a generous 60s ceiling so we
   *  never silently strand the user on an empty list, and we log timing + counts
   *  so support can diagnose stalls from the device console. */
  const readContacts = useCallback(async (): Promise<DeviceContact[]> => {
    setIsLoading(true);
    const startedAt = Date.now();
    try {
      if (!isNative) return [];
      const plugin = await getPlugin();
      if (!plugin) {
        console.warn('[useDeviceContacts] readContacts: plugin unavailable');
        return [];
      }

      const current = await checkPermission();
      if (!canReadNativeContacts(current)) {
        console.info('[useDeviceContacts] readContacts: permission not granted', current);
        return [];
      }

      const TIMEOUT_MS = 60_000;
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

      const getPromise = plugin
        .getContacts({ projection: { name: true, phones: true } })
        .then((r) => {
          settled = true;
          if (timeoutHandle) clearTimeout(timeoutHandle);
          console.info('[useDeviceContacts] getContacts resolved', {
            ms: Date.now() - startedAt,
            count: r.contacts?.length ?? 0,
          });
          return r;
        })
        .catch((err) => {
          settled = true;
          if (timeoutHandle) clearTimeout(timeoutHandle);
          console.error('[useDeviceContacts] getContacts threw:', err);
          return { contacts: [] as NativeContact[] };
        });

      const timeoutPromise = new Promise<{ contacts: NativeContact[] }>((resolve) => {
        timeoutHandle = setTimeout(() => {
          if (settled) return;
          console.error(
            '[useDeviceContacts] getContacts timed out after',
            TIMEOUT_MS,
            'ms - large address book or wedged bridge',
          );
          resolve({ contacts: [] });
        }, TIMEOUT_MS);
      });

      const result = await Promise.race([getPromise, timeoutPromise]);

      const raw = result.contacts || [];
      const parsed: DeviceContact[] = raw
        .map((c) => ({
          name: c.name?.display
            || [c.name?.given, c.name?.family].filter(Boolean).join(' ')
            || c.name?.given
            || c.name?.family
            || 'Unknown',
          phoneNumbers: (c.phones || [])
            .map((p) => (p?.number || '').trim())
            .filter((n) => n.length > 0),
        }))
        .filter((c) => c.phoneNumbers.length > 0);

      console.info('[useDeviceContacts] readContacts done', {
        ms: Date.now() - startedAt,
        rawCount: raw.length,
        withPhones: parsed.length,
        permission: current,
      });

      setContacts(parsed);
      setPermission(current);
      return parsed;
    } catch (err) {
      console.error('[useDeviceContacts] readContacts unexpected error:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [getPlugin, checkPermission]);

  /** Fetch contacts from device. On web, opens the Contact Picker dialog. */
  const fetchContacts = useCallback(async (): Promise<DeviceContact[]> => {
    setIsLoading(true);
    try {
      if (isNative) {
        const current = await checkPermission();
        const allowed = canReadNativeContacts(current) ? current : await requestPermission();
        if (!canReadNativeContacts(allowed)) return [];
        setPermission(allowed);
        return await readContacts();
      }

      // Web Contact Picker API
      if (hasWebContactPicker()) {
        const nav = navigator as WebContactsNavigator;
        const props = ['name', 'tel'];
        const opts = { multiple: true };
        const selected = await nav.contacts?.select(props, opts);
        const parsed: DeviceContact[] = (selected || [])
          .filter((c) => c.tel && c.tel.length > 0)
          .map((c) => ({
            name: c.name?.[0] || 'Unknown',
            phoneNumbers: c.tel || [],
          }));
        setContacts(parsed);
        setPermission('granted');
        return parsed;
      }

      return [];
    } catch (err) {
      console.error('fetchContacts error:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [checkPermission, requestPermission, readContacts]);

  const findElefaFriends = useCallback(async (deviceContactsList?: DeviceContact[]): Promise<MatchedUser[]> => {
    setIsLoading(true);
    setProgress(0);
    try {
      const list = deviceContactsList || contacts;
      if (list.length === 0) { setMatches([]); return []; }

      const phoneSet = new Set<string>();
      list.forEach(c => c.phoneNumbers.forEach(p => {
        const normalized = normalizePhone(p);
        if (normalized.length >= 8) phoneSet.add(normalized);
      }));
      const uniquePhones = Array.from(phoneSet);

      const BATCH = 500;
      const allHashes: string[] = [];
      for (let i = 0; i < uniquePhones.length; i += BATCH) {
        const batch = uniquePhones.slice(i, i + BATCH);
        const hashes = await Promise.all(batch.map(sha256));
        allHashes.push(...hashes);
        setProgress(Math.round((allHashes.length / uniquePhones.length) * 50));
      }

      setProgress(60);

      const { data, error } = await supabase.functions.invoke('match-contacts', {
        body: { hashes: allHashes },
      });

      setProgress(100);

      if (error) {
        console.error('match-contacts error:', error);
        setMatches([]);
        return [];
      }

      const matched: MatchedUser[] = data?.matches || [];
      setMatches(matched);
      return matched;
    } catch (err) {
      console.error('findElefaFriends error:', err);
      setMatches([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [contacts]);

  /**
   * Open the native picker so the user picks ONE contact. On iOS this should
   * use CNContactPickerViewController without requesting full address-book
   * authorization first; scripts/patch-capacitor-contacts-ios.mjs corrects the
   * community plugin because its upstream iOS picker is permission-gated.
   */
  const pickContactNatively = useCallback(async (): Promise<DeviceContact | null> => {
    if (!isNative) {
      // Web fallback - use the Contact Picker API if available
      if (hasWebContactPicker()) {
        try {
          const nav = navigator as WebContactsNavigator;
          const selected = await nav.contacts?.select(['name', 'tel'], { multiple: false });
          const c = selected?.[0];
          if (!c) return null;
          return {
            name: c.name?.[0] || 'Unknown',
            phoneNumbers: c.tel || [],
          };
        } catch {
          return null;
        }
      }
      return null;
    }
    try {
      const plugin = await getPlugin();
      if (!plugin) return null;
      const result = await plugin.pickContact({
        projection: { name: true, phones: true },
      });
      const c = result?.contact;
      if (!c) return null;
      const phones = (c.phones || []).map((p) => p.number).filter(Boolean) as string[];
      if (phones.length === 0) return null;
      return {
        name: c.name?.display || [c.name?.given, c.name?.family].filter(Boolean).join(' ') || 'Unknown',
        phoneNumbers: phones,
      };
    } catch (err) {
      console.error('[useDeviceContacts] pickContactNatively error:', err);
      return null;
    }
  }, [getPlugin]);

  /**
   * Save a contact natively to the device address book (iOS Contacts / Android).
   * Returns true on success. Requires contacts permission.
   */
  const saveToDeviceContacts = useCallback(async (input: {
    name: string;
    phoneNumber: string;
    email?: string;
  }): Promise<boolean> => {
    if (!isNative) {
      console.warn('[useDeviceContacts] saveToDeviceContacts only supported on native');
      return false;
    }
    try {
      const plugin = await getPlugin();
      if (!plugin) return false;
      const perm = await plugin.checkPermissions();
      if (!canReadNativeContacts(mapNativeContactPermission(perm.contacts))) {
        const req = await plugin.requestPermissions();
        if (!canReadNativeContacts(mapNativeContactPermission(req.contacts))) return false;
      }
      const [given, ...rest] = input.name.trim().split(/\s+/);
      const family = rest.join(' ');
      await plugin.createContact({
        contact: {
          name: { given: given || input.name, family: family || undefined },
          phones: [{ type: 'mobile', label: 'mobile', number: normalizePhone(input.phoneNumber) }],
          emails: input.email ? [{ type: 'home', label: 'home', address: input.email }] : undefined,
        },
      });
      setPermission('granted');
      return true;
    } catch (err) {
      console.error('[useDeviceContacts] saveToDeviceContacts error:', err);
      return false;
    }
  }, [getPlugin]);

  return {
    isSupported,
    isNative,
    permission,
    contacts,
    matches,
    isLoading,
    progress,
    checkPermission,
    requestPermission,
    fetchContacts,
    findElefaFriends,
    saveToDeviceContacts,
    pickContactNatively,
    readContacts,
  };
}
